import {
	checkIfIsDentalMedicine,
	checkIfIsPhysiotherapyOrDentalMedicine,
} from 'controllers/TaskManager/TaskSubControllers/TaskDetailsControllers/detailsUtils'
import { parseDate } from 'models/utils'
import { getWorkingDaysBetweenDates } from 'utils/getWorkingDaysBetweenDates'
import { dateTimeFormat } from 'localization'

export const useRequisitionInfo = (requisition, i18n) => {
	const mapMedicalActsDetails = (nextAppointment) => {
		const isPhysiotherapyOrDentalMedicine =
			checkIfIsPhysiotherapyOrDentalMedicine(requisition)

		const isDentalMedicine = checkIfIsDentalMedicine(requisition)

		const details = [
			{
				title: i18n`Prescribing doctor`,
				text: requisition?.prescribingDoctor || '-',
				icon: 'medical-info',
			},
			{
				title: i18n`Requiring provider`,
				text: requisition?.requiringEstablishment?.name || '-',
				icon: 'location-alt',
			},
			{
				title: isPhysiotherapyOrDentalMedicine
					? i18n`Start of treatment`
					: i18n`Booking date`,
				text: requisition?.scheduledDate?.shortDateFormat || '-',
				icon: 'calendar',
			},
		]

		if (isPhysiotherapyOrDentalMedicine) {
			details.push({
				title: i18n`End of treatment`,
				text: requisition?.executionDate?.shortDateFormat || '-',
				icon: 'calendar',
			})
		}

		const currentDate = parseDate(new Date().toISOString())
		const workingDays = getWorkingDaysBetweenDates(
			currentDate?.date?.momentDate,
			nextAppointment?.date?.momentDate
		)

		details.push(
			{
				title: i18n`Next Appointment`,
				text: nextAppointment?.date?.shortDateFormat
					? workingDays !== -1
						? i18n`${
								nextAppointment.date.shortDateFormat
						  } (in ${workingDays} ${
								workingDays === 1
									? i18n`working day`
									: i18n`working days`
						  })`
						: i18n`Without scheduled appointment`
					: '-',
				icon: 'next',
			},
			{
				title: i18n`Transport`,
				text: requisition?.transport || '-',
				icon: 'car',
			},
			{
				title: i18n`Clinical Information`,
				text: requisition?.clinicalInformation || '-',
				icon: 'clinical-notes',
			},
			{
				title: i18n`Observations`,
				text: requisition?.requisitionObservation || '-',
				icon: 'requisition',
			},
			{
				title: i18n`Non execution reason`,
				text: requisition?.nonExecutionMotive || '-',
				icon: 'requisition',
			}
		)

		if (isDentalMedicine) {
			details.push({
				title: i18n`Costs forecast`,
				text: requisition?.amountsToCharge?.expectedValue
					? `${requisition.amountsToCharge.expectedValue} €`
					: '-',
				icon: 'euro',
			})
		}

		return details
	}

	const mapSurgeryClinicalApprovalRequestDetails = (nextAppointment) => {
		const currentDate = parseDate(new Date().toISOString())
		const workingDays = getWorkingDaysBetweenDates(
			currentDate?.date?.momentDate,
			nextAppointment?.date?.momentDate
		)

		return [
			{
				title: i18n`Next appointment`,
				text: nextAppointment?.date?.shortDateFormat
					? workingDays !== -1
						? i18n`${
								nextAppointment.date.shortDateFormat
						  } (in ${workingDays} ${
								workingDays === 1
									? i18n`working day`
									: i18n`working days`
						  })`
						: i18n`Without scheduled appointment`
					: '-',
				icon: 'next',
			},
			{
				title: i18n`Requiring entity`,
				text: requisition?.requiringEstablishment?.name || '-',
				icon: 'location-alt',
			},
			{
				title: i18n`Transport`,
				text: requisition?.transport || '-',
				icon: 'car',
			},
			{
				title: i18n`Prescribing doctor`,
				text: requisition?.prescribingDoctor || '-',
				icon: 'medical-info',
			},
			{
				title: i18n`Clinical Information`,
				text: requisition?.clinicalInformation || '-',
				icon: 'clinical-notes',
			},
			{
				title: i18n`Observations`,
				text: requisition?.requisitionObservation || '-',
				icon: 'requisition',
			},
			{
				title: i18n`Non execution reason`,
				text: requisition?.nonExecutionMotive || '-',
				icon: 'requisition',
			},
		]
	}

	const mapNonSurgicalAdmissionDetails = () => {
		const newDate =
			requisition?.surgicalRequestAdmission?.admissionDate?.momentDate
				.clone()
				.add(
					requisition?.surgicalRequestAdmission?.admissionDays || 0,
					'd'
				)
				.format(dateTimeFormat.date || 'MMM DD, YYYY')

		const details = [
			{
				title: i18n`Destination provider`,
				text: requisition?.establishment?.name || '-',
			},
			{
				title: i18n`Internment`,
				text: i18n`from ${requisition?.surgicalRequestAdmission?.admissionDate?.shortDateFormat} to ${newDate} (${requisition?.surgicalRequestAdmission?.admissionDays} days)`,
			},
		]

		return details
	}

	const mapSurgeryDetails = () => {
		const details = [
			{
				title: i18n`Surgery Date`,
				text:
					requisition?.surgicalRequestAdmission?.surgeryDate
						?.shortDateFormat || '-',
			},
			{
				title: i18n`Destination provider`,
				text: requisition?.establishment?.name || '-',
			},
			{
				title: i18n`Surgery Type`,
				text:
					requisition?.surgicalRequestAdmission?.surgeryType?.name ||
					'-',
			},
			{
				title: i18n`Requires Block`,
				text: i18n([
					requisition?.surgicalRequestAdmission?.needsOperatingRoom ||
						'-',
				]),
			},
		]

		if (requisition?.surgicalRequestAdmission?.surgeryType?.id === 6) {
			const newDate =
				requisition?.surgicalRequestAdmission?.admissionDate?.momentDate
					.clone()
					.add(
						requisition?.surgicalRequestAdmission?.admissionDays ||
							0,
						'd'
					)
					.format(dateTimeFormat.date || 'MMM DD, YYYY')

			details.push({
				title: i18n`Internment`,
				text: i18n`from ${requisition?.surgicalRequestAdmission?.admissionDate?.shortDateFormat} to ${newDate} (${requisition?.surgicalRequestAdmission?.admissionDays} days)`,
			})
		}

		details.push({
			title: i18n`Surgery materials`,
			text: requisition?.materials || '-',
		})

		return details
	}

	const mapMedicalActsListDetails = () => {
		return (requisition?.medicalActs || []).map(
			({
				code,
				name,
				rejected,
				laterality,
				requestedQuantity,
				approvedQuantity,
				executedQuantity,
			}) => ({
				title: code,
				subTitle: name,
				enabled: !rejected,
				value: `${i18n.translate(
					`${laterality}-laterality`
				)} ${requestedQuantity}/${approvedQuantity} (${
					executedQuantity || '-'
				})`,
			})
		)
	}

	const mapPreOperativeListDetails = () => {
		return (requisition?.exams || []).map(({ code, name, rejected }) => ({
			title: code,
			subTitle: name,
			enabled: !rejected,
		}))
	}

	const mapSurgeryListDetails = () => {
		return (requisition?.medicalActs || []).map(
			({ code, name, refused, kValue, percentage }) => ({
				title: code,
				subTitle: name,
				enabled: !refused,
				value: `K:${kValue} ${percentage}%`,
			})
		)
	}

	const mapAdditionalMedicalActsDetailsForPhysiotherapy = () => {
		const details = [
			{
				title: i18n`Post-Surgery`,
				text: requisition?.physiotherapyData?.postSurgery
					? i18n`Yes`
					: i18n`No`,
			},
		]

		if (requisition?.physiotherapyData?.postSurgery) {
			details.push(
				{
					title: i18n`Performed surgery`,
					text:
						requisition?.physiotherapyData?.performedSurgery || '-',
				},
				{
					title: i18n`Surgery date`,
					text:
						requisition?.physiotherapyData?.surgeryDate
							?.shortDateFormat || '-',
				}
			)
		}

		details.push(
			{
				title: i18n`Osteosynthesis Material`,
				text: requisition?.physiotherapyData?.osteosynthesisMaterial
					? i18n`Yes`
					: i18n`No`,
			},
			{
				title: i18n`Immobilized body area`,
				text: requisition?.physiotherapyData?.immobilizationBodyArea
					? i18n`Yes`
					: i18n`No`,
			}
		)

		if (requisition?.physiotherapyData?.immobilizationBodyArea) {
			details.push({
				title: i18n`Immobilization duration`,
				text:
					i18n`${requisition?.physiotherapyData?.immobilizationDuration} dias` ||
					'-',
			})
		}

		details.push({
			title: i18n`Orthoses`,
			text: requisition?.physiotherapyData?.orthoses
				? i18n`Yes`
				: i18n`No`,
		})

		if (requisition?.physiotherapyData?.orthoses) {
			details.push({
				title: i18n`Orthoses weaning since`,
				text:
					requisition?.physiotherapyData?.orthosesFrom
						?.shortDateFormat || '-',
			})
		}

		details.push({
			title: i18n`Walking auxiliaries`,
			text: requisition?.physiotherapyData?.walkingAuxiliaries
				? i18n`Yes`
				: i18n`No`,
		})

		if (requisition?.physiotherapyData?.walkingAuxiliaries) {
			details.push(
				{
					title: i18n`Partial walking auxiliaries weaning since`,
					text:
						requisition?.physiotherapyData?.partialWeaning
							?.shortDateFormat || '-',
				},
				{
					title: i18n`Full walking auxiliaries weaning since`,
					text:
						requisition?.physiotherapyData?.totalWeaning
							?.shortDateFormat || '-',
				}
			)
		}

		return details
	}

	return {
		getMedicalActsDetails: mapMedicalActsDetails,
		getSurgeryClinicalApprovalDetails:
			mapSurgeryClinicalApprovalRequestDetails,
		getSurgeryDetails: mapSurgeryDetails,
		getAdmissionDetails: mapNonSurgicalAdmissionDetails,
		getPhysiotherapyDetails:
			mapAdditionalMedicalActsDetailsForPhysiotherapy,
		getMedicalActsListDetails: mapMedicalActsListDetails,
		getPreOperativeListDetails: mapPreOperativeListDetails,
		getSurgeryListDetails: mapSurgeryListDetails,
	}
}
